// Display helpers shared by the Payments page, drawer and dialog.

import type { PaymentUserRow } from "../api/types";

export const HOUR_MS = 3_600_000;

/** "12h", "12h 30m", "45m", and "-2h" for an overpaid (negative) balance. */
export function formatBalance(ms: number): string {
  const sign = ms < 0 ? "-" : "";
  const totalMin = Math.round(Math.abs(ms) / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${sign}${m}m`;
  if (m === 0) return `${sign}${h}h`;
  return `${sign}${h}h ${String(m).padStart(2, "0")}m`;
}

export function formatWholeHours(hours: number): string {
  return `${hours}h`;
}

/** Full timestamp with the year: statements are read months later. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Payout rail label ("pix" -> "PIX"), or null when the payment does not record one. */
export function methodLabel(method: string | null | undefined): string | null {
  if (!method) return null;
  return method.toLowerCase() === "pix" ? "PIX" : method;
}

export function userLabel(u: Pick<PaymentUserRow, "display_name" | "email" | "uid">): string {
  return u.display_name || u.email || u.uid;
}

export function initials(u: Pick<PaymentUserRow, "display_name" | "email" | "uid">): string {
  const src = u.display_name || u.email || u.uid;
  const parts = src.replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "?";
}

/** `datetime-local` value for "now" in the browser's zone (seconds dropped). */
export function nowLocalInputValue(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

/** Convert a `datetime-local` value back to an ISO string carrying the browser's offset. */
export function localInputToIso(value: string): string {
  return new Date(value).toISOString();
}

export type BalanceState = "payable" | "under_hour" | "settled" | "overpaid";

export function balanceState(u: PaymentUserRow): BalanceState {
  if (u.pending_ms < 0) return "overpaid";
  if (u.pending_ms === 0) return "settled";
  return u.payable_hours >= 1 ? "payable" : "under_hour";
}
